import { Icon, IconName } from '@usealloy/component-library';
import React from 'react';
import styled from 'styled-components';

import { SidebarButtonBase } from './SidebarButtonBase';
import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
interface ExpandToggleProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'children'> {}

// ============================================================
//     STYLES
// ============================================================
const StyledToggle = styled(SidebarButtonBase)({
  position: 'absolute',
  top: 'var(--cl-space-6)', // 24px down from the top
  right: 0,
  transform: 'translateX(50%)', // 50% overhang past the sidebar's right edge
  // Paints above the adjacent main content the overhang floats over
  zIndex: 1,

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 'var(--cl-space-6)', // 24px
  width: 'var(--cl-space-6)',
  minWidth: 'var(--cl-space-6)',
  padding: 0,
  borderRadius: 'var(--cl-radius-circle)',

  '--btn-bg': 'var(--cl-background-default)',
  '--btn-bg-hover': 'var(--cl-gray-200)',
  '--btn-bg-active': 'var(--cl-gray-300)',
  border: '1px solid var(--cl-border-default)',
  boxShadow: '0 1px 3px oklch(0 0 0 / 0.1)',
});

const StyledIconWrapper = styled.span({
  display: 'inline-flex',
  transform: 'rotate(180deg)',
});

// ============================================================
//     COMPONENT
// ============================================================
/**
 * Floating expand/collapse action button for the sidebar.
 *
 * Renders as a small round button straddling the sidebar's right edge (50%
 * overhang), near the top. The caret points left while expanded and rotates to
 * point right when collapsed.
 *
 * Render it as a direct child of `<Sidebar>` (NOT inside `Sidebar.Body`, whose
 * `overflow: clip` would swallow the overhang). Only useful alongside
 * `isCollapsible`.
 *
 * @example
 * ```tsx
 * <Sidebar isCollapsible>
 *   <Sidebar.ExpandToggle />
 *   <Sidebar.Body>...</Sidebar.Body>
 * </Sidebar>
 * ```
 */
export const ExpandToggle = (props: ExpandToggleProps) => {
  const { onClick, ...restProps } = props;
  const { isExpanded, toggleSidebar } = useSidebarCtx();

  return (
    <StyledToggle
      type="button"
      {...restProps}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
      data-component="Sidebar.ExpandToggle"
      data-expanded={isExpanded}
      data-icon-only=""
      onClick={event => {
        onClick?.(event);
        toggleSidebar();
      }}
    >
      <StyledIconWrapper data-expanded={isExpanded}>
        <Icon icon={IconName.SidebarFlip} size="small" color="var(--cl-gray-900)" />
      </StyledIconWrapper>
    </StyledToggle>
  );
};

ExpandToggle.displayName = 'Sidebar.ExpandToggle' as const;
